import React, { useState, useEffect, useRef } from 'react';
import { Send, Sparkles, FileText, RefreshCw, Download, FileDown } from 'lucide-react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useToastContext } from '@/contexts/ToastContext';
import { TOTAL_WEEKS } from '@/utils/dateUtils';
import { apiClient } from '@/api/client';
import { weeklyReportService, exportService } from '@/api/services';
import { useAppStore } from '@/store';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  suggestions?: string[];
  ready?: boolean;
  // Set on the "report enhanced" message so it can offer the export link.
  canExport?: boolean;
  // Renders as a full-width "NEW SESSION" divider instead of a chat bubble.
  separator?: boolean;
}

interface Conversation {
  id: string;
  title: string;
  messages: Message[];
  sessionId?: string;
  isComplete?: boolean;
}

interface AiEnhanceResponse {
  success: boolean;
  session_id?: string | null;
  is_complete?: boolean;
  ready_to_enhance?: boolean;
  already_enhanced?: boolean;
  replay?: boolean;
  message_id?: number;
  ai_message?: string | null;
  question?: string | null;
  suggestions?: string[] | null;
  enhanced_data?: Record<string, unknown> | null;
  message?: string;
  error?: string;
  code?: string;
  recommendations?: string[];
}

interface HistoryMessage {
  id: number;
  role: 'user' | 'assistant';
  content: string;
  message_type: string;
  is_pending: boolean;
  meta?: { suggestions?: string[]; enhanced_data?: Record<string, unknown> };
  created_at: string;
}

interface HistoryResponse {
  success: boolean;
  session?: {
    id: number;
    session_id: string;
    status: string;
    week_number: number;
    updated_at: string;
  } | null;
  messages: HistoryMessage[];
  pending?: boolean;
  already_enhanced?: boolean;
}

// Claude turns can take a while (Django waits on the AI service), so these
// requests get a larger budget than the default client timeout.
const AI_REQUEST_TIMEOUT = 60000;

const buildAssistantMessage = (
  content: string,
  seed: number,
  extra?: { suggestions?: string[]; ready?: boolean; canExport?: boolean }
): Message => ({
  id: `ai-${seed}`,
  role: 'assistant',
  content,
  timestamp: new Date().toISOString(),
  suggestions: extra?.suggestions,
  ready: extra?.ready,
  canExport: extra?.canExport
});

// The finished report always carries the "export" link with it.
const buildCompleteMessage = (
  enhancedData: Record<string, any> | null | undefined,
  seed: number
): Message =>
  buildAssistantMessage(buildEnhancementSummary(enhancedData), seed, { canExport: true });

const buildSeparatorMessage = (seed: number): Message => ({
  id: `sep-${seed}`,
  role: 'assistant',
  content: 'NEW SESSION',
  timestamp: new Date().toISOString(),
  separator: true
});

const messageFromHistory = (m: HistoryMessage): Message => ({
  id: `db-${m.id}`,
  role: m.role,
  separator: m.message_type === 'separator',
  // A finished enhancement replays the full enhanced report, not the short
  // "enhancement completed" placeholder that was stored alongside it.
  content:
    m.message_type === 'separator'
      ? 'NEW SESSION'
      : m.role === 'assistant' && m.message_type === 'complete' && m.meta?.enhanced_data
        ? buildEnhancementSummary(m.meta.enhanced_data as Record<string, any>)
        : m.content,
  timestamp: m.created_at,
  suggestions: Array.isArray(m.meta?.suggestions) && m.meta?.suggestions?.length
    ? m.meta.suggestions
    : undefined,
  ready: m.message_type === 'ready',
  canExport: m.role === 'assistant' && m.message_type === 'complete'
});

const messageFromResponse = (data: AiEnhanceResponse, seed: number): Message => {
  if (data.is_complete && data.enhanced_data) {
    return buildCompleteMessage(data.enhanced_data, seed);
  }
  return buildAssistantMessage(formatAiMessage(data), seed, {
    suggestions: Array.isArray(data.suggestions) && data.suggestions.length
      ? data.suggestions
      : undefined,
    ready: Boolean(data.ready_to_enhance)
  });
};

const formatAiMessage = (data: AiEnhanceResponse): string => {
  const aiMessage = (data.ai_message || '').trim();
  const question = (data.question || '').trim();
  if (aiMessage && question && aiMessage !== question) {
    return `${aiMessage}\n\n${question}`;
  }
  return question || aiMessage || 'How can I help you enhance this report?';
};

const buildEnhancementSummary = (enhancedData: Record<string, any> | null | undefined): string => {
  const days = Array.isArray(enhancedData?.daily_reports) ? enhancedData.daily_reports : [];
  const operations = Array.isArray(enhancedData?.operations) ? enhancedData.operations : [];
  const totalHours = days.reduce(
    (sum: number, day: any) => sum + (Number(day?.hours_worked) || 0),
    0
  );

  const lines = ['Your weekly report has been enhanced and saved.'];
  if (enhancedData?.main_job_title) {
    lines.push(`Main job: ${enhancedData.main_job_title}`);
  }
  lines.push(
    `${days.length} daily ${days.length === 1 ? 'entry' : 'entries'} improved` +
      (totalHours > 0 ? ` (${Number(totalHours.toFixed(1))} hours total)` : '') +
      '.'
  );

  // Full report, exactly as enhanced - nothing is shortened for display.
  if (days.length > 0) {
    lines.push('', 'Daily reports:');
    days.forEach((day: any) => {
      const heading = [day?.day, day?.date ? `(${day.date})` : '']
        .filter(Boolean)
        .join(' ');
      const hours = Number(day?.hours_worked);
      lines.push(
        '',
        `${heading}${Number.isFinite(hours) ? ` - ${hours} hours` : ''}`,
        String(day?.description || '').trim() || 'No description recorded.'
      );
    });
  }

  if (operations.length > 0) {
    lines.push('', 'Main job operations:');
    operations.forEach((op: any, index: number) => {
      const step = op?.step_number ?? index + 1;
      lines.push('', `${step}. ${String(op?.operation_description || '').trim()}`);
      if (op?.tools_used) {
        lines.push(`Tools: ${String(op.tools_used).trim()}`);
      }
    });
  }

  return lines.join('\n');
};

const getErrorMessage = (error: unknown, fallback: string): string => {
  const serverMessage = (error as any)?.response?.data?.message;
  if (typeof serverMessage === 'string' && serverMessage.trim()) {
    return serverMessage.trim();
  }
  // Errors thrown from a success:false body carry their own message.
  if ((error as any)?.fromErrorBody && typeof (error as any)?.message === 'string'
    && (error as any).message.trim()) {
    return (error as any).message.trim();
  }
  return fallback;
};

// Backend failures carry actionable next steps - surface them instead of
// leaving the user staring at an error with no way forward.
const getErrorRecommendations = (error: unknown): string[] => {
  const recs = (error as any)?.response?.data?.recommendations ?? (error as any)?.recommendations;
  return Array.isArray(recs)
    ? recs.filter((r: unknown): r is string => typeof r === 'string' && r.trim().length > 0)
    : [];
};

const buildErrorContent = (message: string, recommendations: string[]): string =>
  recommendations.length > 0
    ? `${message}\n\n${recommendations.map(r => `- ${r}`).join('\n')}`
    : message;

// The backend forgets a session when the AI service restarts or the row is
// rebuilt - the browser then holds a dead id. Recover by starting a fresh
// session instead of dead-ending the chat.
const isSessionLostError = (error: unknown): boolean => {
  const data = (error as any)?.response?.data;
  if (data?.code === 'SESSION_NOT_FOUND') return true;
  const message = getErrorMessage(error, '');
  return /session not found/i.test(message);
};

// A success:false body arrives with HTTP 200, so the catch blocks never see it -
// throw it while keeping the recommendations reachable.
const apiError = (data: AiEnhanceResponse, fallback: string): Error & { recommendations?: string[] } => {
  const error = new Error(data.message || data.error || fallback) as Error & {
    recommendations?: string[];
    fromErrorBody?: boolean;
  };
  error.recommendations = data.recommendations ?? [];
  error.fromErrorBody = true;
  return error;
};

// Export endpoints answer with blobs, so their error payloads arrive as blobs
// too - read the message out instead of showing a broken download.
const readBlobErrorPayload = async (error: unknown): Promise<any | null> => {
  const data = (error as any)?.response?.data;
  if (!(data instanceof Blob)) return (error as any)?.response?.data ?? null;
  try {
    return JSON.parse(await data.text());
  } catch {
    return null;
  }
};

const exportErrorMessage = async (error: unknown, fallback: string): Promise<string> => {
  const data = (error as any)?.response?.data;
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text());
      const message = parsed?.message || parsed?.detail ||
        (parsed?.error ? String(parsed.error) : null);
      if (message) return message;
    } catch {
      // Not JSON - fall through to the generic message.
    }
  }
  return getErrorMessage(error, fallback);
};

// WhatsApp style: numbered items each on their own line, text hanging under
// the number instead of wrapping back to the left margin.
const NUMBERED_RE = /^\s*(\d+)[.)]\s+(.*)$/;
const BULLET_RE = /^\s*[-•*]\s+(.*)$/;

const renderRichText = (content: string): React.ReactNode[] => {
  const nodes: React.ReactNode[] = [];
  const lines = (content || '').split('\n');
  let list: { key: string; marker?: string; text: string }[] = [];
  let bulletList: { key: string; text: string }[] = [];
  let blockKey = 0;

  const flushLists = () => {
    if (list.length > 0) {
      nodes.push(
        <ol key={`ol-${blockKey++}`} className="my-1.5 space-y-1.5">
          {list.map(item => (
            <li key={item.key} className="flex gap-2 pl-1">
              <span className="shrink-0 font-semibold tabular-nums opacity-80">{item.marker}.</span>
              <span className="min-w-0 flex-1 whitespace-pre-wrap break-words">{item.text}</span>
            </li>
          ))}
        </ol>
      );
      list = [];
    }
    if (bulletList.length > 0) {
      nodes.push(
        <ul key={`ul-${blockKey++}`} className="my-1.5 space-y-1.5">
          {bulletList.map(item => (
            <li key={item.key} className="flex gap-2 pl-1">
              <span className="shrink-0 opacity-80">•</span>
              <span className="min-w-0 flex-1 whitespace-pre-wrap break-words">{item.text}</span>
            </li>
          ))}
        </ul>
      );
      bulletList = [];
    }
  };

  lines.forEach((line, index) => {
    const numbered = line.match(NUMBERED_RE);
    if (numbered) {
      flushLists();
      list.push({ key: `n-${index}`, marker: numbered[1], text: numbered[2] });
      return;
    }
    const bullet = line.match(BULLET_RE);
    if (bullet) {
      flushLists();
      bulletList.push({ key: `b-${index}`, text: bullet[1] });
      return;
    }
    flushLists();
    if (line.trim() === '') {
      if (nodes.length > 0) nodes.push(<div key={`sp-${index}`} className="h-2" />);
      return;
    }
    nodes.push(
      <p key={`p-${index}`} className="whitespace-pre-wrap break-words">{line}</p>
    );
  });
  flushLists();

  return nodes.length > 0 ? nodes : [<p key="empty">{content}</p>];
};

interface WeekEnhancementCacheEntry {
  conversation: Conversation;
  notice: string | null;
}

/** In-tab cache so leaving /enhancement and returning does not replay the intro. */
const weekEnhancementCache = new Map<string, WeekEnhancementCacheEntry>();

const isActiveEnhancementCache = (entry?: WeekEnhancementCacheEntry | null): boolean =>
  Boolean(
    entry?.conversation &&
    !entry.conversation.isComplete &&
    entry.conversation.messages.length > 0 &&
    entry.conversation.sessionId
  );

const pickMessagesForResume = (fromHistory: Message[], cached?: Message[] | null): Message[] => {
  const cachedMsgs = cached ?? [];
  if (fromHistory.length >= cachedMsgs.length) return fromHistory;
  return cachedMsgs;
};

// The assistant's current session state decides completeness. A "complete"
// message from an earlier enhancement must NOT mark the thread as done when
// the user has since started a new session (backend status returns to
// IN_PROGRESS, i.e. already_enhanced is false).
const historyIndicatesComplete = (history: HistoryResponse): boolean =>
  Boolean(history.already_enhanced);

const restoreConversationFromHistory = (
  weekNumber: string,
  history: HistoryResponse,
  restored: Message[]
): { conversation: Conversation; notice: string | null; isComplete: boolean } => {
  const title = `Enhance Week ${weekNumber}`;
  const sessionId = history.session?.session_id;
  const isComplete = historyIndicatesComplete(history);

  let notice: string | null = null;
  if (isComplete) {
    notice = history.session?.updated_at
      ? `Week ${weekNumber} was already enhanced on ${new Date(history.session.updated_at).toLocaleString()}.`
      : `Week ${weekNumber} was already enhanced.`;
  }

  return {
    notice: isComplete ? notice : null,
    isComplete,
    conversation: {
      id: `enhancement-${weekNumber}-${history.session?.session_id || 'local'}`,
      title,
      messages: restored,
      sessionId: sessionId || undefined,
      isComplete
    }
  };
};

const Chatbot: React.FC = () => {
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [selectedWeek, setSelectedWeek] = useState<string | null>(null);
  const [showWeekSelector, setShowWeekSelector] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const { userBalance, fetchUserBalance } = useAppStore();
  // Whether this week's report has been AI-enhanced (export costs 200 vs 100).
  const [reportEnhanced, setReportEnhanced] = useState(false);
  const [exporting, setExporting] = useState<'pdf' | 'docx' | null>(null);
  // Message id whose "click here to export" link is currently expanded.
  const [exportMenuFor, setExportMenuFor] = useState<string | null>(null);
  const [viewportHeight, setViewportHeight] = useState<number>(() =>
    (window.visualViewport?.height ?? window.innerHeight) + (window.visualViewport?.offsetTop ?? 0)
  );
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const initSeqRef = useRef(0);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showSuccess, showError } = useToastContext();

  // Track the visible viewport so the composer sits directly above the
  // on-screen keyboard (visualViewport shrinks when the keyboard opens).
  useEffect(() => {
    const vv = window.visualViewport;
    const update = () =>
      setViewportHeight((vv?.height ?? window.innerHeight) + (vv?.offsetTop ?? 0));
    update();
    vv?.addEventListener('resize', update);
    vv?.addEventListener('scroll', update);
    window.addEventListener('resize', update);
    return () => {
      vv?.removeEventListener('resize', update);
      vv?.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [conversation, isTyping, viewportHeight]);

  // Grow with the text and collapse back to a single line when it is emptied.
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    const maxHeight = window.innerHeight * 0.3;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, maxHeight)}px`;
  }, [input]);

  useEffect(() => {
    const weekParam = searchParams.get('week');
    if (weekParam) {
      setSelectedWeek(weekParam);
      const cached = weekEnhancementCache.get(weekParam);
      if (cached) {
        setConversation(cached.conversation);
        setNotice(cached.notice);
        setShowWeekSelector(false);
      }
      const seq = ++initSeqRef.current;
      const fastResume = isActiveEnhancementCache(cached);
      void initializeEnhancementConversation(weekParam, false, seq, {
        allowEnhancePost: !fastResume
      });
    } else {
      setShowWeekSelector(true);
    }
  }, [searchParams]);

  useEffect(() => {
    if (!selectedWeek || !conversation) return;
    if (conversation.title !== `Enhance Week ${selectedWeek}`) return;
    weekEnhancementCache.set(selectedWeek, { conversation, notice });
  }, [selectedWeek, conversation, notice]);

  useEffect(() => {
    fetchUserBalance();
  }, [fetchUserBalance]);

  // Export pricing depends on whether the report was AI-enhanced, so keep the
  // flag in sync with the selected week (and refresh once enhancement finishes).
  useEffect(() => {
    if (!selectedWeek) {
      setReportEnhanced(false);
      return;
    }
    let cancelled = false;
    weeklyReportService
      .getWeeklyReportByWeek(parseInt(selectedWeek, 10))
      .then(report => {
        if (!cancelled) setReportEnhanced(Boolean(report?.is_ai_enhanced));
      })
      .catch(() => {
        if (!cancelled) setReportEnhanced(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedWeek, conversation?.isComplete]);

  const exportTokenCost = reportEnhanced ? 200 : 100;
  const availableTokens = userBalance?.available_tokens ?? 0;
  const canAffordExport = userBalance
    ? userBalance.available_tokens >= exportTokenCost
    : true;

  const lastAssistant = [...(conversation?.messages ?? [])]
    .reverse()
    .find(msg => msg.role === 'assistant');
  const readyToEnhance = Boolean(
    !conversation?.isComplete &&
    conversation?.sessionId &&
    lastAssistant?.ready
  );

  const initializeEnhancementConversation = async (
    weekNumber: string,
    restart = false,
    initSeq?: number,
    options?: { allowEnhancePost?: boolean }
  ) => {
    const allowEnhancePost = options?.allowEnhancePost ?? true;

    const runInit = async () => {
      const isStaleInit = () => initSeq !== undefined && initSeq !== initSeqRef.current;
      const cachedEntry = weekEnhancementCache.get(weekNumber);

      if (!restart && cachedEntry?.conversation.messages.length) {
        setConversation(cachedEntry.conversation);
        setNotice(cachedEntry.notice);
        setShowWeekSelector(false);
      }

      setLoading(true);
      setShowWeekSelector(false);

      const convId = `enhancement-${weekNumber}-${Date.now()}`;
      const title = `Enhance Week ${weekNumber}`;
      let restored: Message[] = [];

      try {
        // Show whatever this week's chat already contains before asking for more.
        // Also fetched on restart so the old conversation stays visible above
        // the NEW SESSION divider.
        try {
          const history = await apiClient.get<HistoryResponse>(
            '/reports/weekly/ai/history',
            { params: { week_number: weekNumber }, timeout: AI_REQUEST_TIMEOUT }
          );
          if (isStaleInit()) return;

          restored = (history.data.messages || []).map(messageFromHistory);

          if (!restart) {
            const sessionId =
              history.data.session?.session_id || cachedEntry?.conversation.sessionId;
            const hasHistory = restored.length > 0;
            const isComplete = historyIndicatesComplete(history.data);
            const mergedMessages = pickMessagesForResume(
              restored,
              cachedEntry?.conversation.messages
            );

            if (isComplete) {
              const restoredState = restoreConversationFromHistory(
                weekNumber,
                history.data,
                restored
              );
              setNotice(restoredState.notice);
              setConversation(restoredState.conversation);
              return;
            }

            // Resume whenever the server or tab already has this session — never
            // POST /enhance again (that generates a second intro + question set).
            if (sessionId || hasHistory || mergedMessages.length > 0) {
              if (sessionId || cachedEntry?.conversation.sessionId) {
                setNotice(null);
                setConversation({
                  id: `enhancement-${weekNumber}-${sessionId || cachedEntry?.conversation.sessionId}`,
                  title,
                  messages: mergedMessages,
                  sessionId: sessionId || cachedEntry?.conversation.sessionId,
                  isComplete: false
                });
                return;
              }

              setNotice(
                'Your previous messages are shown below. Start a new session to continue enhancing this report.'
              );
              setConversation({
                id: convId,
                title,
                messages: mergedMessages,
                isComplete: false
              });
              return;
            }
          }
        } catch (historyError) {
          console.warn('Could not load chat history:', historyError);
        }

        if (!restart) {
          if (isActiveEnhancementCache(cachedEntry)) {
            setConversation(cachedEntry!.conversation);
            setNotice(cachedEntry!.notice);
            return;
          }

          const cached = weekEnhancementCache.get(weekNumber);
          if (cached?.conversation.messages.length) {
            setConversation(cached.conversation);
            setNotice(cached.notice);
            return;
          }
        }

        if (!allowEnhancePost) {
          // Fast-resume path: keep the in-tab thread even if history is not synced yet.
          if (cachedEntry?.conversation.messages.length) {
            setConversation(cachedEntry.conversation);
            setNotice(cachedEntry.notice);
          }
          return;
        }

        if (isStaleInit()) return;

        const response = await apiClient.post<AiEnhanceResponse>(
        '/reports/weekly/ai/enhance',
        {
          week_number: parseInt(weekNumber, 10),
          ...(restart ? { restart: true } : {})
        },
        { timeout: AI_REQUEST_TIMEOUT }
      );
      const data = response.data;
      if (isStaleInit()) return;

      if (!data.success) {
        throw apiError(data, 'Failed to start enhancement');
      }

      if (data.already_enhanced) {
        setNotice(data.message || `Week ${weekNumber} was already enhanced.`);
        setConversation({
          id: convId,
          title,
          messages: restored,
          sessionId: data.session_id || undefined,
          isComplete: true
        });
        return;
      }

      const seed = Date.now();
      const alreadyStored = Boolean(
        data.replay &&
        data.message_id &&
        restored.some(msg => msg.id === `db-${data.message_id}`)
      );
      const messages = [...restored];
      if (restart && restored.length > 0) {
        // The backend stores the same divider in history; show it right away.
        messages.push(buildSeparatorMessage(seed));
      }
      if (!alreadyStored) {
        messages.push(messageFromResponse(data, seed + 1));
      }

      const isComplete = Boolean(data.is_complete && data.enhanced_data);
      if (isStaleInit()) return;

      setNotice(null);
      setConversation({
        id: convId,
        title,
        sessionId: data.session_id || undefined,
        isComplete,
        messages
      });

      if (isComplete) {
        showSuccess('Weekly report enhanced and saved!');
      }
    } catch (error) {
      if (isStaleInit()) return;
      console.error('Error initializing enhancement:', error);
      const message = getErrorMessage(
        error,
        'Sorry, I could not start the enhancement right now. Please make sure the backend and AI service are running, then try again.'
      );
      const recommendations = getErrorRecommendations(error);
      showError(message);
      const seed = Date.now();
      setConversation({
        id: convId,
        title,
        messages: [
          ...restored,
          buildAssistantMessage(buildErrorContent(message, recommendations), seed)
        ]
      });
    } finally {
      setLoading(false);
    }
  };

  await runInit();
  };

  const handleWeekSelect = (weekNumber: string) => {
    setSelectedWeek(weekNumber);
    navigate(`/enhancement?week=${weekNumber}`);
  };

  const startNewConversation = () => {
    setShowWeekSelector(true);
    setSelectedWeek(null);
    setConversation(null);
    setNotice(null);
    setInput('');
    setExportMenuFor(null);
    navigate('/enhancement');
  };

  const startAnotherEnhancementSession = () => {
    if (!selectedWeek || loading) return;
    setNotice(null);
    setInput('');
    setExportMenuFor(null);
    void initializeEnhancementConversation(selectedWeek, true);
  };

  const sendMessage = async (textOverride?: string) => {
    const text = (textOverride ?? input).trim();
    if (!text || loading || !conversation || !selectedWeek) return;
    if (conversation.isComplete) return;
    if (!conversation.sessionId) {
      showError('No active enhancement session. Please start again.');
      return;
    }

    setInput('');
    setLoading(true);
    setIsTyping(true);

    const userMessage: Message = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: text,
      timestamp: new Date().toISOString()
    };

    setConversation(prev => prev ? {
      ...prev,
      messages: [...prev.messages, userMessage]
    } : null);

    textareaRef.current?.focus();

    try {
      const response = await apiClient.post<AiEnhanceResponse>(
        '/reports/weekly/ai/answer',
        { session_id: conversation.sessionId, answer: text },
        { timeout: AI_REQUEST_TIMEOUT }
      );
      const data = response.data;

      if (!data.success) {
        throw apiError(data, 'Enhancement failed');
      }

      const seed = Date.now();
      if (data.is_complete && data.enhanced_data) {
        setConversation(prev => prev ? {
          ...prev,
          isComplete: true,
          // The session may have been rebuilt server-side (expired/restarted
          // AI service) - keep the id it replies with.
          sessionId: data.session_id || prev.sessionId,
          messages: [...prev.messages, buildCompleteMessage(data.enhanced_data, seed)]
        } : null);
        showSuccess('Weekly report enhanced and saved!');
      } else {
        setConversation(prev => prev ? {
          ...prev,
          sessionId: data.session_id || prev.sessionId,
          messages: [...prev.messages, messageFromResponse(data, seed)]
        } : null);
      }
    } catch (error) {
      console.error('AI enhancement error:', error);

      if (isSessionLostError(error)) {
        // The stored session id no longer exists server-side. Rebuild it and
        // put the user back in front of a fresh set of questions.
        showError('Your enhancement session was lost, so I started a new one.');
        setNotice('Your previous session expired, so a new one was started. Please answer the questions again.');
        setConversation(prev => prev ? { ...prev, sessionId: undefined } : null);
        if (selectedWeek) {
          await initializeEnhancementConversation(selectedWeek, true);
        }
        return;
      }

      const message = getErrorMessage(
        error,
        'Sorry, I encountered an error while processing your request. Please make sure the AI service is running and try again.'
      );
      showError(message);
      setConversation(prev => prev ? {
        ...prev,
        messages: [
          ...prev.messages,
          buildAssistantMessage(buildErrorContent(message, getErrorRecommendations(error)), Date.now())
        ]
      } : null);
    } finally {
      setLoading(false);
      setIsTyping(false);
    }
  };

  const confirmEnhancement = async () => {
    if (loading || !conversation?.sessionId || conversation.isComplete) return;

    const sessionId = conversation.sessionId;
    setLoading(true);
    setIsTyping(true);

    const userMessage: Message = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: 'Enhance report',
      timestamp: new Date().toISOString()
    };
    setConversation(prev => prev ? {
      ...prev,
      messages: [...prev.messages, userMessage]
    } : null);

    try {
      const response = await apiClient.post<AiEnhanceResponse>(
        '/reports/weekly/ai/answer',
        { session_id: sessionId, confirm: true },
        { timeout: AI_REQUEST_TIMEOUT }
      );
      const data = response.data;

      if (!data.success) {
        throw apiError(data, 'Enhancement failed');
      }

      if (data.is_complete && data.enhanced_data) {
        const seed = Date.now();
        setConversation(prev => prev ? {
          ...prev,
          isComplete: true,
          sessionId: data.session_id || prev.sessionId,
          messages: [...prev.messages, buildCompleteMessage(data.enhanced_data, seed)]
        } : null);
        showSuccess('Weekly report enhanced and saved!');
      } else {
        showError('The report could not be enhanced yet. Please answer the remaining questions.');
      }
    } catch (error) {
      console.error('AI enhancement error:', error);

      if (isSessionLostError(error)) {
        showError('Your enhancement session was lost, so I started a new one.');
        setNotice('Your previous session expired, so a new one was started. Please answer the questions again.');
        setConversation(prev => prev ? { ...prev, sessionId: undefined } : null);
        if (selectedWeek) {
          await initializeEnhancementConversation(selectedWeek, true);
        }
        return;
      }

      const message = getErrorMessage(
        error,
        'Sorry, I encountered an error while enhancing the report. Please try again.'
      );
      showError(message);
      setConversation(prev => prev ? {
        ...prev,
        messages: [
          ...prev.messages,
          buildAssistantMessage(buildErrorContent(message, getErrorRecommendations(error)), Date.now())
        ]
      } : null);
    } finally {
      setLoading(false);
      setIsTyping(false);
    }
  };

  const showSuggestions =
    !conversation?.isComplete &&
    !loading &&
    !input.trim() &&
    !lastAssistant?.ready &&
    Boolean(lastAssistant?.suggestions?.length);

  const exportReport = async (format: 'pdf' | 'docx') => {
    if (!selectedWeek || exporting) return;

    // Block the export up front when the balance cannot cover it. The backend
    // enforces this too, but this avoids a wasted generation round-trip.
    if (!canAffordExport) {
      showError(
        `Not enough tokens to export this ${reportEnhanced ? 'enhanced' : 'normal'} report. ` +
        `It costs ${exportTokenCost} tokens and you have ${availableTokens}. ` +
        `Please top up your balance.`
      );
      return;
    }

    setExporting(format);
    try {
      const week = parseInt(selectedWeek, 10);
      let blob: Blob;
      try {
        blob =
          format === 'pdf'
            ? await weeklyReportService.downloadWeeklyReportPDF(week)
            : await weeklyReportService.downloadWeeklyReportDOCX(week);
      } catch (primaryError) {
        // An insufficient-token response must not trigger the fallback route:
        // that route charges again.
        const primaryPayload = await readBlobErrorPayload(primaryError);
        if (primaryPayload?.requires_tokens) {
          throw new Error(primaryPayload.message || 'Not enough tokens to export this report.');
        }

        // Same fallback the report details page uses: the id based export route.
        const report = await weeklyReportService.getWeeklyReportByWeek(week);
        if (!report?.id) throw primaryError;
        blob =
          format === 'pdf'
            ? await exportService.exportWeeklyPDF(report.id)
            : await exportService.exportWeeklyDOCX(report.id);
      }

      if (!blob || blob.size === 0) {
        throw new Error('The server returned an empty document.');
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `week-${selectedWeek}-report.${format}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setExportMenuFor(null);
      showSuccess(
        `Week ${selectedWeek} report downloaded as ${format.toUpperCase()}. ` +
        `${exportTokenCost} tokens deducted.`
      );
      // The download endpoints deducted server-side, so refresh the balance.
      fetchUserBalance();
    } catch (error) {
      console.error('Report export failed:', error);
      showError(await exportErrorMessage(error, `Could not download the ${format.toUpperCase()} report. Please try again.`));
      fetchUserBalance();
    } finally {
      setExporting(null);
    }
  };

  return (
    <div
      className="flex flex-col h-[calc(var(--chat-vv,100dvh)-5rem)] lg:h-[calc(var(--chat-vv,100vh)-4rem)]"
      style={{ '--chat-vv': `${viewportHeight}px` } as React.CSSProperties}
    >
      <div className="relative flex-1 min-h-0 flex flex-col bg-white/60 backdrop-blur border border-orange-200/50 rounded-2xl shadow-xl overflow-hidden">
        {/* Top bar: always visible, never scrolls with the messages */}
        <header className="shrink-0 flex items-center justify-between gap-2 px-4 sm:px-6 py-3 bg-orange-50 border-b border-orange-200">
          <div className="flex items-center gap-2 min-w-0 text-sm text-orange-700">
            <FileText className="w-4 h-4 shrink-0" />
            {selectedWeek ? (
              <Link
                to={`/weekly-report/${selectedWeek}`}
                title={`Back to Week ${selectedWeek} report details`}
                className="font-semibold underline underline-offset-2 decoration-orange-300 hover:decoration-orange-600 hover:text-orange-800 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400 min-w-0 truncate"
              >
                Back to Week {selectedWeek} Report
              </Link>
            ) : (
              <span className="font-medium truncate">AI Report Assistant</span>
            )}
          </div>
          <button
            onClick={startNewConversation}
            className="shrink-0 border-2 border-orange-300 text-orange-700 hover:bg-orange-50 hover:border-orange-500 transition-all rounded-lg px-3 h-8 text-xs font-medium flex items-center"
          >
            New
          </button>
        </header>

        {/* Body */}
        <div className="flex-1 min-h-0">
          {showWeekSelector ? (
            <div className="h-full overflow-y-auto p-6 sm:p-8">
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-gradient-to-br from-orange-500 to-orange-600 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-xl">
                  <FileText className="w-10 h-10 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Select a Week to Enhance</h2>
                <p className="text-gray-600">Choose which weekly report you'd like to enhance with AI assistance</p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {Array.from({ length: TOTAL_WEEKS }, (_, i) => i + 1).map((week) => (
                  <button
                    key={week}
                    onClick={() => handleWeekSelect(week.toString())}
                    className="p-4 bg-white border-2 border-orange-200 rounded-xl hover:border-orange-500 hover:bg-orange-50 transition-all shadow-sm hover:shadow-md"
                  >
                    <div className="text-lg font-bold text-orange-600">Week {week}</div>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div
              ref={scrollRef}
              className="h-full overflow-y-auto space-y-4 p-4 sm:p-6 bg-gradient-to-br from-orange-50 to-amber-50 select-none"
              onCopy={(e) => e.preventDefault()}
              onCut={(e) => e.preventDefault()}
            >
              {notice && (
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 border border-amber-200 bg-amber-50 text-amber-800 rounded-xl px-4 py-3 text-sm">
                  <span className="flex-1">{notice}</span>
                  <button
                    onClick={startAnotherEnhancementSession}
                    disabled={loading}
                    className="shrink-0 inline-flex items-center gap-2 border border-amber-300 bg-white hover:bg-amber-100 rounded-lg px-3 h-8 text-xs font-medium transition-all disabled:opacity-50"
                  >
                    <RefreshCw className="w-3.5 h-3.5" />
                    Start another session
                  </button>
                </div>
              )}

              {conversation?.messages?.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center px-4">
                  <div className="w-20 h-20 bg-gradient-to-br from-orange-500 to-orange-600 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-xl">
                    <Sparkles className="w-10 h-10 text-white" />
                  </div>
                  <p className="text-gray-600">Start enhancing your report</p>
                </div>
              ) : (
                conversation?.messages.map((msg, index) => {
                  const isLast = index === (conversation.messages.length - 1);
                  const showChips = isLast && showSuggestions && msg.role === 'assistant';
                  if (msg.separator) {
                    return (
                      <div
                        key={msg.id}
                        role="separator"
                        className="flex items-center gap-3 py-1 select-none"
                      >
                        <div className="h-px flex-1 bg-orange-300/70" />
                        <span className="text-[11px] font-semibold tracking-widest text-orange-500 uppercase">
                          {msg.content || 'New session'}
                        </span>
                        <div className="h-px flex-1 bg-orange-300/70" />
                      </div>
                    );
                  }
                  return (
                    <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div className="flex flex-col items-start gap-2 max-w-[85%] sm:max-w-[75%]">
                        <MessageBubble msg={msg} />
                        {msg.role === 'assistant' && msg.canExport && (
                          <div className="w-full rounded-2xl border border-orange-200 bg-white px-3 py-2.5 shadow-sm">
                            <button
                              type="button"
                              onClick={() =>
                                setExportMenuFor(prev => (prev === msg.id ? null : msg.id))
                              }
                              className="inline-flex items-center gap-1.5 text-xs font-semibold text-orange-600 hover:text-orange-700 underline underline-offset-2"
                            >
                              <Download className="w-3.5 h-3.5" />
                              Click here to export the report ({exportTokenCost} tokens)
                            </button>
                            {!canAffordExport && (
                              <p className="mt-1.5 text-[11px] text-red-600">
                                Not enough tokens. You need {exportTokenCost} but only have {availableTokens}.
                              </p>
                            )}
                            {exportMenuFor === msg.id && (
                              <div className="mt-2.5 flex flex-col gap-2">
                                <button
                                  type="button"
                                  onClick={() => exportReport('pdf')}
                                  disabled={Boolean(exporting) || !canAffordExport}
                                  className="flex items-center gap-2 border-2 border-orange-200 hover:border-orange-500 hover:bg-orange-50 rounded-xl px-3 py-2 text-left transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                                >
                                  <FileDown className="w-4 h-4 text-orange-600 shrink-0" />
                                  <span className="min-w-0">
                                    <span className="block text-xs font-semibold text-gray-900">PDF</span>
                                    <span className="block text-[11px] text-gray-500">
                                      Best for sharing and printing · {exportTokenCost} tokens
                                    </span>
                                  </span>
                                  {exporting === 'pdf' && (
                                    <RefreshCw className="w-3.5 h-3.5 ml-auto text-orange-600 animate-spin shrink-0" />
                                  )}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => exportReport('docx')}
                                  disabled={Boolean(exporting) || !canAffordExport}
                                  className="flex items-center gap-2 border-2 border-orange-200 hover:border-orange-500 hover:bg-orange-50 rounded-xl px-3 py-2 text-left transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                                >
                                  <FileText className="w-4 h-4 text-orange-600 shrink-0" />
                                  <span className="min-w-0">
                                    <span className="block text-xs font-semibold text-gray-900">Word document (DOCX)</span>
                                    <span className="block text-[11px] text-gray-500">
                                      Best for editing further · {exportTokenCost} tokens
                                    </span>
                                  </span>
                                  {exporting === 'docx' && (
                                    <RefreshCw className="w-3.5 h-3.5 ml-auto text-orange-600 animate-spin shrink-0" />
                                  )}
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                        {showChips && (
                          <div className="flex flex-wrap gap-2">
                            {lastAssistant?.suggestions?.map((suggestion, i) => (
                              <button
                                key={`${msg.id}-sug-${i}`}
                                onClick={() => sendMessage(suggestion)}
                                disabled={loading}
                                className="border border-orange-300 bg-white text-orange-700 hover:bg-orange-50 rounded-full px-3 py-1.5 text-xs font-medium transition-all disabled:opacity-50"
                              >
                                {suggestion}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
              {isTyping && (
                <div className="flex justify-start">
                  <div className="bg-white border border-orange-100 rounded-2xl rounded-bl-none px-4 py-3 shadow-sm">
                    <div className="flex items-center gap-2 text-gray-500">
                      <div className="w-2 h-2 bg-orange-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <div className="w-2 h-2 bg-orange-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <div className="w-2 h-2 bg-orange-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Composer: pinned to the bottom, rises above the keyboard */}
        {!showWeekSelector && (
          <div className="shrink-0 p-4 sm:p-6 bg-white border-t border-gray-200/50">
            {readyToEnhance && (
              <div className="mb-3">
                <button
                  onClick={confirmEnhancement}
                  disabled={loading}
                  className="w-full bg-gradient-to-br from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-2xl px-4 h-11 font-semibold text-sm shadow-lg transition-all disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  <Sparkles className="w-4 h-4" />
                  {loading ? 'Enhancing your report…' : 'Enhance report'}
                </button>
              </div>
            )}
            {conversation?.isComplete && (
              <div className="mb-3 space-y-2">
                <p className="text-[11px] text-gray-500 text-center">
                  Enhancement complete. Export from the link under the summary, or start a new session to
                  refine the report further.
                </p>
                <button
                  type="button"
                  onClick={startAnotherEnhancementSession}
                  disabled={loading}
                  className="w-full inline-flex items-center justify-center gap-2 border-2 border-orange-300 text-orange-700 hover:bg-orange-50 rounded-2xl px-4 h-11 text-sm font-semibold transition-all disabled:opacity-60"
                >
                  <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                  Start another session
                </button>
              </div>
            )}
            <div className="flex items-end gap-3">
              <div className="flex-1 bg-gray-50 border border-gray-200 rounded-2xl px-4 py-2 focus-within:border-orange-500 focus-within:bg-white transition-all shadow-sm">
                <textarea
                  ref={textareaRef}
                  rows={1}
                  disabled={conversation?.isComplete}
                  placeholder={
                    conversation?.isComplete
                      ? 'Report enhancement complete.'
                      : readyToEnhance
                        ? 'Add anything else, or press Enhance report…'
                        : 'Type your message...'
                  }
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      sendMessage();
                    }
                  }}
                  className="min-h-[40px] max-h-[30vh] resize-none border-0 focus:border-0 focus:ring-0 focus:outline-none px-0 py-1 text-sm bg-transparent w-full disabled:opacity-60 disabled:cursor-not-allowed"
                />
              </div>
              <button
                onClick={() => sendMessage()}
                disabled={loading || !input.trim() || !conversation || Boolean(conversation?.isComplete)}
                className="shrink-0 bg-gradient-to-br from-orange-500 to-orange-600 hover:shadow-lg text-white h-12 w-12 rounded-2xl p-0 transition-all disabled:opacity-50 flex items-center justify-center"
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const MessageBubble: React.FC<{ msg: Message }> = ({ msg }) => (
  <div className={`rounded-2xl px-4 py-3 text-sm leading-relaxed shadow-sm transition-all hover:shadow-md ${
    msg.role === 'user'
      ? 'bg-gradient-to-br from-orange-500 to-orange-600 text-white rounded-br-none'
      : 'bg-white border border-orange-100 text-gray-900 rounded-bl-none'
  }`}>
    {renderRichText(msg.content)}
  </div>
);

export { Chatbot };
export default Chatbot;
